/**
 * Composed goal-recipe catalog entries for the law industry (plan §6.3 /
 * §6.5). Filled by the law P4b copywriting task — owned exclusively by it.
 * Entries omit `previewPath` (added automatically in template-catalog.ts).
 *
 * Copy discipline (plan §3.6 / task spec): owner-facing vocabulary (the
 * office's 所長 voice, not marketing jargon), and strict adherence to 士業
 * advertising rules — no outcome guarantees, no success-rate claims, no
 * "必ず解決" / "勝てます" framing (same no-guarantee posture as
 * `content-packs/law/*.ts`'s `casesDisclaimer`). `features` / `concerns`
 * only name things the composed seed actually generates for that goal (see
 * `../../../apps/cms/src/seeds/goal-recipes/{trust,discovery,booking}.ts`
 * and `../../../apps/cms/src/seeds/adapters/law.ts`) — the law adapter's
 * `access`/`hours` roles always return `null` (no ContentPack carries
 * structured address/hours data), so none of these three entries mention
 * location/hours as a feature, and none promise a booking calendar or
 * online-meeting flow the seed does not create.
 */
import type { TemplateEntryDraft } from '../template-catalog';

export const COMPOSED_LAW_TEMPLATES: readonly TemplateEntryDraft[] = [
  {
    slug: 'law-trust',
    theme: 'law',
    displayName: '実績と人柄で選ばれる事務所サイト',
    category: '士業・コンサル',
    description:
      '事務所案内ページで代表者の経歴と人柄を伝え、解決事例ページで取扱実績を紹介する構成。「どんな人が相談に乗ってくれるのか」を知ってから問い合わせてもらいたい事務所に向いています。',
    thumb: '/lp/template-legal.webp',
    previewFixture: 'law-demo',
    fit: '代表者の経歴や実績を前面に出して相談・依頼につなげたい法律事務所・税理士・会計事務所・行政書士・社労士・司法書士・コンサルタント',
    features: [
      '代表者の経歴・人柄を伝える事務所案内ページ',
      '解決事例ページ（結果保証ではない旨の注記つき）',
      '選ばれる理由セクション',
      '相談フォーム付きの相談CTA（トップと専用ページの両方）',
    ],
    goal: 'trust',
    industry: 'law',
    subIndustries: [
      '法律事務所',
      '税理士・会計事務所',
      '行政書士',
      '社会保険労務士',
      '司法書士',
      'コンサルティング',
    ],
    outcomeStory:
      '初めて事務所のサイトを訪れた人は、まず代表者の経歴とご挨拶を読み、次に解決事例で「自分と似た相談に対応した実績があるか」を確かめます。トップページの案内から事務所案内・解決事例の各ページへ迷わず進めるので、問い合わせフォームにたどり着くころには「この事務所に相談してみよう」という気持ちが整っています。',
    concerns: [
      {
        worry: '経歴や実績をどう見せればいいかわからない',
        answer:
          '代表者の経歴・所属・解決事例を書き込むだけで、事務所案内ページと解決事例ページが自動で整った形になります。文章はそのまま公開できる初期文が入っているので、書き直すところから始めなくて済みます。',
      },
      {
        worry: '事例を載せると「必ず解決できる」と誤解されないか不安',
        answer:
          '解決事例には「同様の結果を保証するものではありません」という注記が最初から入っています。実績は伝えつつ、誇大な期待を持たれない書き方になっています。',
      },
      {
        worry: '開業後、事例やお知らせを追加し続けられるか心配',
        answer:
          'かんたん更新なら、新しい解決事例やお知らせをスマホから1分で追加できます。文章を書いたら「サイトを公開」ボタンを押すだけで、すぐにサイトへ反映されます。',
      },
      {
        worry: '相談フォームからの問い合わせを取りこぼさないか不安',
        answer:
          '相談フォームにはスパム対策と通知メールが最初から設定されています。万一システムに障害が起きても、フォームでの相談受付は止まりません。',
      },
    ],
    businessFlow: [
      '朝、スマホでダッシュボードを開き、直近の解決事例を1件追加する',
      '「サイトを公開」ボタンを押すと、数分でサイトに反映される',
      '相談フォームからの問い合わせが通知メールで届く',
      '独自ドメインを設定している場合は、そのドメインのままサイトが表示され続ける（プランにより利用可能）',
    ],
  },
  {
    slug: 'law-discovery',
    theme: 'law',
    displayName: '検索から取扱業務を知ってもらうサイト',
    category: '士業・コンサル',
    description:
      'トップページに取扱業務の抜粋とお知らせを並べ、検索や紹介で来た人に「何を頼める事務所か」をすぐ伝える構成。事務所を探している人に業務内容が伝わることを優先したい方に向いています。',
    thumb: '/lp/template-tax.webp',
    previewFixture: 'tax-demo',
    fit: '検索や紹介で来た人に取扱業務をすぐ伝えたい法律事務所・税理士・会計事務所・行政書士・社労士・司法書士・コンサルタント',
    features: [
      '選ばれる理由セクション',
      '取扱業務の抜粋紹介',
      'お知らせ欄（コラム・法改正情報の掲載にも利用可能）',
      '電話番号を前面に出した相談CTA',
    ],
    goal: 'discovery',
    industry: 'law',
    subIndustries: [
      '法律事務所',
      '税理士・会計事務所',
      '行政書士',
      '社会保険労務士',
      '司法書士',
      'コンサルティング',
    ],
    outcomeStory:
      '検索や知人の紹介で事務所名を知った人が最初に見るのはトップページです。選ばれる理由と取扱業務の抜粋がすぐ目に入り、「この事務所は自分の相談に対応できそうだ」とわかります。お知らせ欄を更新し続けることで、次に検索したときにも新しい情報が出ている状態を保てます。',
    concerns: [
      {
        worry: 'ホームページを作っても検索で見つけてもらえるか不安',
        answer:
          'ページの表示は高速で、取扱業務やお知らせがテキストとして掲載されるので、検索エンジンが内容を読み取りやすい状態で公開されます。',
      },
      {
        worry: '業務内容をどう文章にすればいいかわからない',
        answer:
          '取扱業務の紹介文はそのまま公開できる初期文が入っています。ご自身の事務所の得意分野に合わせて、そのまま使うか一部だけ書き換えるかを選べます。',
      },
      {
        worry: 'お知らせや法改正コラムを更新し続けられるか心配',
        answer:
          'かんたん更新なら、お知らせやコラムをスマホから1分で追加できます。書いたら「サイトを公開」ボタンを押すだけで反映されます。',
      },
    ],
    businessFlow: [
      '法改正情報や事務所からのお知らせをスマホで書く',
      '「サイトを公開」ボタンを押すと、その日のうちにサイトへ反映される',
      '電話でのお問い合わせに対応する',
      '独自ドメインを設定していれば、そのドメインのままサイトが表示され続ける（プランにより利用可能）',
    ],
  },
  {
    slug: 'law-booking',
    theme: 'law',
    displayName: '初回相談の予約につなげるサイト',
    category: '士業・コンサル',
    description:
      'トップページの上部と下部の両方に相談予約のCTAを配置し、取扱業務の全体像を専用ページで見せる構成。「まず初回相談を予約してほしい」事務所に向いています。',
    thumb: '/lp/template-labor.webp',
    previewFixture: 'labor-demo',
    fit: '初回相談の予約を増やしたい法律事務所・税理士・会計事務所・行政書士・社労士・司法書士・コンサルタント',
    features: [
      'トップページ上部・下部の2箇所に配置された相談予約CTA',
      '取扱業務の全体を紹介する専用ページ',
      '相談予約フォーム付きの専用ページ（スパム対策・通知メールつき）',
      '事務所案内（代表者紹介）',
    ],
    goal: 'booking',
    industry: 'law',
    subIndustries: [
      '法律事務所',
      '税理士・会計事務所',
      '行政書士',
      '社会保険労務士',
      '司法書士',
      'コンサルティング',
    ],
    outcomeStory:
      'トップページを開いた瞬間に相談予約への案内が目に入り、業務内容を確認したあとにも同じ案内がもう一度出てくるので、迷っているうちに離脱しにくくなります。相談予約ページにはフォームが埋め込まれているので、電話が苦手な人もそのまま送信して初回相談の申し込みを完了できます。',
    concerns: [
      {
        worry: '相談予約フォームを自分で用意する余裕がない',
        answer:
          '相談予約フォームは最初から用意されており、スパム対策と通知メールも設定済みです。送信されると担当者にメールで届きます。',
      },
      {
        worry: 'システムに障害が起きたときに予約を受け付けられなくなるか心配',
        answer:
          '万一障害が起きても、相談予約フォームでの受付は止まりません。お客様からの相談を取りこぼしにくい仕組みです。',
      },
      {
        worry: '業務内容の説明文を自分で書く時間がない',
        answer:
          '取扱業務の紹介文や事務所案内の文章は、そのまま公開できる初期文が入っています。まずは公開してから、少しずつ自分の事務所らしく書き換えられます。',
      },
      {
        worry: '独自ドメインでの運用ができるか気になる',
        answer:
          '独自ドメインでの公開に対応しています（プランによって利用可否が異なります）。すでにお持ちのドメインをそのまま使えます。',
      },
    ],
    businessFlow: [
      '相談予約フォームからの申し込みを通知メールで確認する',
      '取扱業務ページの内容をスマホで見直し、必要であれば更新する',
      '「サイトを公開」ボタンを押すと、変更がすぐにサイトへ反映される',
      '事務所案内ページの経歴・お知らせを定期的に見直す',
    ],
  },
];
